import { useI18n } from '@ecom/i18n';
import { SUPPORTED_LOCALES, type Locale } from '@ecom/i18n/locales';
import { type UseThemeResult } from '@ecom/ui-kit';
import { type ReactElement, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { openCommandPalette } from './CommandPalette';
import { ShopSwitcher } from './ShopSwitcher';

import { openAssistant } from '@/features/assistant/open';
import { PushBell } from '@/features/orders/components/PushBell';
import { ViewShopButton } from '@/features/share/components/ViewShopButton';
import type { UseActiveShopResult } from '@/features/shops/use-active-shop';
import type { LiveStatus } from '@/lib/live-updates';
import { useLocale } from '@/lib/locale';
import { useSession } from '@/lib/session';

/**
 * Top bar, in two groups:
 *   - start: the menu button (below `lg`, where the sidebar is hidden), then the shop — its initial, its name, and the
 *     « En direct » status of the live updates;
 *   - end: small round icon buttons (phone notifications, light/dark, language), « Voir la boutique », and the account
 *     menu, which holds « Mon compte » and « Se déconnecter » (and, on a phone, the shop name, theme and language).
 * No search field: the command palette stays one shortcut away (⌘K / Ctrl+K).
 */

const LANGUAGE_NAMES: Record<Locale, string> = { fr: 'Français', ar: 'العربية', en: 'English' };
const LANGUAGE_SHORT: Record<Locale, string> = { fr: 'FR', ar: 'ع', en: 'EN' };

const ICON_BUTTON = 'sk-icon-btn';

function SearchIcon(): ReactElement {
  return (
    <SvgIcon>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-4-4" />
    </SvgIcon>
  );
}

/**
 * The top bar of the Figma design (« Souk 2.0 »): sticky and translucent over the canvas, 76px (64px on a phone).
 * Start: the menu button (below 900px), then the shop — its initials tile, its name (the shop switcher when the
 * account has several shops) and the live status of the updates stream under it. Middle: the search field, which opens
 * the command palette (Ctrl+K). End: the assistant, language, light/dark, phone notifications, « Voir la boutique »
 * and the profile button with the account menu. Every control is the one that was here before, re-dressed; on a phone
 * the ones that do not fit move into the account menu, never away.
 */
export function Topbar({
  onOpenNav,
  navOpen,
  theme: themeState,
  shop,
  liveStatus,
}: {
  onOpenNav: () => void;
  navOpen: boolean;
  theme: UseThemeResult;
  shop: UseActiveShopResult;
  liveStatus: LiveStatus;
}): ReactElement {
  // `toggleTheme` is called via `themeState.toggleTheme` rather than
  // destructured — see the identical note in CommandPalette.tsx.
  const { theme } = themeState;
  const { clearSession } = useSession();
  const { locale, setLocale } = useLocale();
  const { t } = useI18n();
  const navigate = useNavigate();

  const liveLabel = t(
    liveStatus === 'live'
      ? 'merchant.live.on'
      : liveStatus === 'connecting'
        ? 'merchant.live.connecting'
        : 'merchant.live.off',
  );
  const signOut = (): void => {
    clearSession();
    void navigate('/login', { replace: true });
  };
  const toggleTheme = (): void => {
    themeState.toggleTheme();
  };
  const themeLabel =
    theme === 'dark' ? t('merchant.topbar.lightMode') : t('merchant.topbar.darkMode');
  const shopName =
    shop.memberships.find((membership) => membership.tenantId === shop.activeShopId)?.legalName ??
    null;
  const initials = (shopName ?? 'S')
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('');
  const roleLabel = t(
    shop.activeMembership?.roleKey === 'merchant_owner'
      ? 'merchant.staff.roleOwner'
      : 'merchant.staff.roleStaff',
  );

  return (
    <header className="app-topbar sk-topbar">
      <button
        type="button"
        onClick={onOpenNav}
        aria-label={t('merchant.topbar.menu')}
        aria-expanded={navOpen}
        className={`${ICON_BUTTON} min-[901px]:hidden`}
      >
        <SvgIcon>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </SvgIcon>
      </button>

      <div className="sk-store">
        <span aria-hidden="true" className="sk-store-avatar">
          {initials}
        </span>
        <div className="sk-store-name">
          <div className="min-w-0 max-w-[40vw] sm:max-w-[14rem] [&_select]:h-auto [&_select]:border-0 [&_select]:bg-transparent [&_select]:p-0 [&_select]:font-display [&_select]:text-sm [&_select]:font-bold [&_span]:font-display [&_span]:text-sm [&_span]:font-bold">
            <ShopSwitcher shop={shop} />
          </div>
          <span className="sk-store-status" title={t('merchant.live.hint')} role="status">
            {/* The one looping motion of the account: the dot breathes while the live stream is connected. */}
            <i
              aria-hidden="true"
              className={
                liveStatus === 'live'
                  ? 'live-dot'
                  : liveStatus === 'connecting'
                    ? 'is-connecting'
                    : 'is-off'
              }
            />
            {liveLabel}
          </span>
        </div>
      </div>

      <button type="button" className="sk-search" onClick={openCommandPalette}>
        <SearchIcon />
        <span>{t('merchant.topbar.searchPlaceholder')}</span>
        <kbd>Ctrl K</kbd>
      </button>

      <div className="sk-top-actions">
        {shop.activeShopId !== null ? (
          <button
            type="button"
            onClick={openAssistant}
            aria-label={t('merchant.assistant.open')}
            title={t('merchant.assistant.open')}
            className={`${ICON_BUTTON} sk-hide-phone`}
          >
            <SparkleIcon />
          </button>
        ) : null}
        <LanguageMenu
          locale={locale}
          onChange={(next) => {
            setLocale(next);
          }}
        />
        <button
          type="button"
          onClick={toggleTheme}
          title={themeLabel}
          aria-label={t('merchant.topbar.themeToggleAria')}
          className={`${ICON_BUTTON} sk-hide-phone`}
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
        <PushBell />
        <span className="sk-hide-phone sk-viewshop">
          <ViewShopButton />
        </span>
        <AccountMenu
          label={shopName ?? t('account.nav')}
          initials={initials}
          name={shopName ?? t('account.nav')}
          role={roleLabel}
        >
          {(close) => (
            <>
              <div className="border-b border-line px-3 pb-2 pt-1 sm:hidden">
                <ShopSwitcher shop={shop} />
              </div>
              {/* On a phone the bar has no room for the assistant's and the shop's own buttons: they open from here. */}
              <MenuItem
                className="sm:hidden"
                onClick={() => {
                  openAssistant();
                  close();
                }}
                icon={<SparkleIcon />}
              >
                {t('merchant.assistant.open')}
              </MenuItem>
              <div className="px-2 py-1 sm:hidden">
                <ViewShopButton />
              </div>
              <MenuLink to="/account" onClick={close} icon={<UserIcon />}>
                {t('account.nav')}
              </MenuLink>
              <MenuItem
                className="sm:hidden"
                onClick={() => {
                  toggleTheme();
                  close();
                }}
                icon={theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              >
                {themeLabel}
              </MenuItem>
              <div className="sk-menu-locales flex gap-1 px-2 py-1">
                {SUPPORTED_LOCALES.map((code) => (
                  <button
                    key={code}
                    type="button"
                    aria-pressed={locale === code}
                    onClick={() => {
                      setLocale(code);
                      close();
                    }}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold ${
                      locale === code
                        ? 'bg-accentSoft text-accentInk'
                        : 'text-inkFaint hover:bg-surfaceHover'
                    }`}
                  >
                    {LANGUAGE_NAMES[code]}
                  </button>
                ))}
              </div>
              <div className="my-1 h-px bg-line" />
              <MenuItem onClick={signOut} icon={<LogoutIcon />} tone="critical">
                {t('merchant.topbar.logout')}
              </MenuItem>
            </>
          )}
        </AccountMenu>
      </div>
    </header>
  );
}

/** « FR ▾ » — the three languages in a small native list (keyboard and screen readers for free). */
function LanguageMenu({
  locale,
  onChange,
}: {
  locale: Locale;
  onChange: (locale: Locale) => void;
}): ReactElement {
  return (
    <label className="sk-locale-wrap relative">
      <span className="sr-only">Langue / Language / اللغة</span>
      <select
        value={locale}
        onChange={(event) => {
          onChange(event.target.value as Locale);
        }}
        className="sk-locale"
      >
        {SUPPORTED_LOCALES.map((code) => (
          <option key={code} value={code}>
            {LANGUAGE_SHORT[code]} · {LANGUAGE_NAMES[code]}
          </option>
        ))}
      </select>
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="pointer-events-none absolute end-2 top-1/2 h-3 w-3 -translate-y-1/2 text-inkFaint"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </label>
  );
}

/** The account menu: a round button with the shop's initial, a small panel closed by a choice, Escape or a tap outside. */
function AccountMenu({
  label,
  initials,
  name,
  role,
  children,
}: {
  label: string;
  /** The profile button of the Figma design: initials, then the name and the role (hidden below 1180px). */
  initials: string;
  name: string;
  role: string;
  children: (close: () => void) => ReactElement;
}): ReactElement {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: PointerEvent): void => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-label={t('merchant.topbar.more')}
        title={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((value) => !value);
        }}
        className="sk-profile"
      >
        <span className="sk-profile-avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="sk-profile-copy">
          <strong className="max-w-[9rem] truncate">{name}</strong>
          <small>{role}</small>
        </span>
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className={`h-3 w-3 text-inkFaint transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <div
          id={panelId}
          className="souk-popover absolute end-0 top-full z-50 mt-3 flex w-60 max-w-[calc(100vw-1.5rem)] flex-col p-1.5"
        >
          {children(() => {
            setOpen(false);
          })}
        </div>
      ) : null}
    </div>
  );
}

const MENU_ROW =
  'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-sm transition-colors hover:bg-surfaceHover';

function MenuItem({
  icon,
  onClick,
  children,
  tone,
  className = '',
}: {
  icon: ReactNode;
  onClick: () => void;
  children: ReactNode;
  tone?: 'critical';
  className?: string;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${MENU_ROW} ${tone === 'critical' ? 'text-critical' : 'text-ink'} ${className}`}
    >
      <span className="text-inkFaint">{icon}</span>
      {children}
    </button>
  );
}

function MenuLink({
  to,
  icon,
  onClick,
  children,
}: {
  to: string;
  icon: ReactNode;
  onClick: () => void;
  children: ReactNode;
}): ReactElement {
  return (
    <Link to={to} onClick={onClick} className={`${MENU_ROW} text-ink`}>
      <span className="text-inkFaint">{icon}</span>
      {children}
    </Link>
  );
}

function SvgIcon({ children }: { children: ReactNode }): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

function SparkleIcon(): ReactElement {
  return (
    <SvgIcon>
      <path d="M12 3.5c.6 3.9 2.6 5.9 6.5 6.5-3.9.6-5.9 2.6-6.5 6.5-.6-3.9-2.6-5.9-6.5-6.5 3.9-.6 5.9-2.6 6.5-6.5Z" />
      <path d="M18.5 15.5c.25 1.6 1 2.35 2.5 2.5-1.5.15-2.25.9-2.5 2.5-.25-1.6-1-2.35-2.5-2.5 1.5-.15 2.25-.9 2.5-2.5Z" />
    </SvgIcon>
  );
}

function MoonIcon(): ReactElement {
  return (
    <SvgIcon>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </SvgIcon>
  );
}

function SunIcon(): ReactElement {
  return (
    <SvgIcon>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </SvgIcon>
  );
}

function UserIcon(): ReactElement {
  return (
    <SvgIcon>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </SvgIcon>
  );
}

function LogoutIcon(): ReactElement {
  return (
    <SvgIcon>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </SvgIcon>
  );
}
